/**
 * The two browser deletes that take Google locations out of Fly record them
 * first, through gbp-agent-portal (WO-J2, Anand, 15 Oct 26: a removed Google
 * location cannot come back on the free trial for 6 months;
 * supabase/functions/_shared/gbp-agent-location-removal-ops.ts):
 *
 *   recordOpsRemovals        the ops command center's Delete workspace and
 *                            Delete location (UsersWorkspacesManagement.tsx),
 *                            ops_location_removals_record, Fly staff only
 *   recordWorkspaceRemovals  Delete workspace in the workspace settings
 *                            (WorkspaceContext.tsx): deleting a workspace
 *                            deletes its locations with it (the foreign key
 *                            cascades), app_workspace_removals_record, the
 *                            workspace owner only
 *
 * Each throws when the record does not go through, so the delete that
 * follows never runs unrecorded: the page's own catch shows why.
 */
import { supabase } from '@/integrations/supabase/client';
import { fnErrorText } from '@/lib/fnError';

export type OpsRemovalSource = 'ops_delete_workspace' | 'ops_delete_location';

/** The line when the record did not go through and nothing was deleted. */
export const REMOVAL_UNRECORDED = 'The removal could not be recorded, so nothing was deleted. Try again in a moment.';

async function record(body: Record<string, unknown>): Promise<number> {
  const { data, error } = await supabase.functions.invoke('gbp-agent-portal', { body });
  if (error) throw new Error(await fnErrorText(error, REMOVAL_UNRECORDED));
  if (!data || data.ok !== true) throw new Error(typeof data?.error === 'string' ? data.error : REMOVAL_UNRECORDED);
  console.log('[recordLocationRemovals] recorded', { op: body.op, recorded: data.recorded });
  return Number(data.recorded ?? 0);
}

export async function recordOpsRemovals(locationIds: string[], source: OpsRemovalSource): Promise<number> {
  const ids = [...new Set(locationIds.filter(Boolean))];
  if (!ids.length) return 0;
  return record({ op: 'ops_location_removals_record', location_ids: ids, source });
}

export async function recordWorkspaceRemovals(workspaceId: string): Promise<number> {
  return record({ op: 'app_workspace_removals_record', workspace_id: workspaceId });
}
